import React, { FC, useState } from 'react';
import { useFormik } from 'formik';
import {
	convertToOwnerInitialValues,
	getFormattedOwnerFormFields,
	getOwnerValidationSchema,
	initialOwnerValues, OWNER_FORM_FIELDS, OWNER_FORM_FIELDS_NAMES, OwnerFieldReturnTypes,
	OwnerFieldsTypes,
} from '../../../constants/merchant/owner-form';
import { useTranslation } from 'react-i18next';
import {
	OWNER_ADDRESS_INPUTS,
	OWNER_CONTACT_INPUTS,
} from '../../../constants/merchant/owner-inputs';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { Title } from '../../developers/page-components/Title';
import { FormInput } from '../FormInput';
import { InputType } from '../../form/form-input-constants';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { errorHandler } from '../../../utils/newApplicationUtils';
import { IOwners } from '../../../interfaces/merchant';

interface Props {
	onClose: () => void,
	onSubmit: (data?: OwnerFieldReturnTypes) => Promise<void> | void,
	onError: () => void,
	ownerId: string | number,
	ownerData: IOwners,
}

export const OwnerModalForm: FC<Props> = (props) => {
	const { onClose, ownerId, ownerData, onSubmit, onError } = props;
	const { t } = useTranslation();
	const { merchantState: { merchantsLists: lists }} = useMerchants();

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const formik = useFormik<OwnerFieldsTypes>({
		initialValues: ownerId !== null && ownerId !== undefined ? convertToOwnerInitialValues(ownerData) : initialOwnerValues,
		validationSchema: getOwnerValidationSchema(t),
		onSubmit: async (): Promise<void> => {
			try {
				setIsLoading(true);
				const formattedData = getFormattedOwnerFormFields(formik.values);

				await onSubmit(formattedData);

				onClose();
				setIsLoading(false);
			} catch (e) {
				console.log('error', e.response);
				if (ownerId) {
					onError();
				} else {
					errorHandler({ e, formik });
					setIsLoading(false);
				}
			}
		}
	});

	const CONTACT_INPUTS = OWNER_CONTACT_INPUTS({ t, lists });

	const ADDRESS_INPUTS = OWNER_ADDRESS_INPUTS({ t, lists, formik });

	return (
		<form onSubmit={formik.handleSubmit} className="px-8">
			<Title title="MerchantOwnerPage information" additionalClass="fw-bolder fs-4 mb-5" />
			<div className="row">
				<FormInput
					formik={formik}
					fieldName={OWNER_FORM_FIELDS.TITLE}
					label={t(OWNER_FORM_FIELDS_NAMES.TITLE)}
					placeholder={`${t('NEW_APPLICATION.ENTER')} ${t(OWNER_FORM_FIELDS_NAMES.TITLE)}`}
					inputType={InputType.Text}
					additionalClasses="col-md-4"
					key={OWNER_FORM_FIELDS.TITLE}
				/>
				<FormInput
					formik={formik}
					fieldName={OWNER_FORM_FIELDS.PRINCIPAL_TYPE}
					label={t(OWNER_FORM_FIELDS_NAMES.PRINCIPAL_TYPE)}
					placeholder={t(OWNER_FORM_FIELDS_NAMES.PRINCIPAL_TYPE)}
					inputType={InputType.Select}
					data={lists?.principalTypes}
					searchable={false}
					additionalClasses="col-md-4"
					key={OWNER_FORM_FIELDS.PRINCIPAL_TYPE}
				/>

				<div className="col-md-4"/>
				{
					CONTACT_INPUTS.map(item => {
						return (
							<FormInput
								{...item}
								key={item.key}
								formik={formik}
							/>
						);
					})
				}
			</div>

			<Title title="MerchantOwnerPage address information" additionalClass="fw-bolder fs-4 mb-5" />
			<div className="row">
				{
					ADDRESS_INPUTS.map(item => {
						return (
							<FormInput
								{...item}
								key={item.key}
								formik={formik}
							/>
						);
					})
				}
			</div>

			<div className="text-center my-5">
				<ButtonComponent
					buttonName="Cancel"
					buttonType={BUTTON_TYPE.CANCELLED}
					additionalClass="btn-sm"
					onButtonClick={onClose}
				/>
				<ButtonComponent
					buttonName={ownerId ? 'Edit' : 'Add'}
					type="submit"
					additionalClass="btn-sm ms-10 w-70px"
					loading={isLoading}
					isDisabled={isLoading}
				/>
			</div>

		</form>
	);
};
